import React, { FC, useMemo } from 'react'
import { useHistory } from 'react-router'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IShipmentBid } from 'types'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics'
import { IMotorCarrier } from 'apps/fleet-planner/types'
import { EditIcon } from 'transfix-icons'
import { Card, IconButton } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import { formatCurrency } from 'utils/formatCurrency'
import { DISPLAY } from 'constants/display'
import { CounterOfferDetails } from './CounterOfferDetails'

export interface IExistingBidProps {
  motorCarriers: IMotorCarrier[]
  shipmentId: number
  bid: IShipmentBid
  route?: string
}

const useExistingBidStyles = makeStyles(() => ({
  bid: {
    color: brandColors.info,
  },
  bidDeclined: {
    color: brandColors.coolGray5,
  },
  editIcon: {
    color: brandColors.skyBlue6,
    position: 'absolute',
    right: '0px',
  },
}))

/**
 * Note: This component is only for pending/declined bids. Bids with an 'accepted' status are
 * considered shipment offers.
 */
export const ExistingBidCard: FC<IExistingBidProps> = ({
  bid,
  motorCarriers,
  route = 'book',
  shipmentId,
}) => {
  const { amount, carrierDotNumber, counterBidAmount, declinedAt, id: bidId, status } = bid || {}

  const { getFlag } = useFeatureFlagContext()
  const { enabled: fmsCounterBiddingEnabled } = getFlag('fmsCounterBidding')

  const classes = useExistingBidStyles()

  const carrierLegalName = useMemo(() => {
    return motorCarriers.find(mc => mc.dotNumber === Number(carrierDotNumber))?.legalName
  }, [carrierDotNumber, motorCarriers])

  const carrierId = useMemo(() => {
    return motorCarriers.find(mc => mc.dotNumber === Number(carrierDotNumber))?.id
  }, [carrierDotNumber, motorCarriers])

  const history = useHistory()
  const { trackEvent } = useFMSAnalytics()

  const clickEditBidHandler = () => {
    history.push(`/${route}/${shipmentId}/bid/${bidId}`)
  }

  const counterOfferDetailsOnClick = () => {
    trackEvent('Book Load', 'ACCEPTED_COUNTER_OFFER_ON_BOOKABLE_LOAD', { bid })
  }

  const offerCounterBid = useMemo(() => fmsCounterBiddingEnabled && counterBidAmount, [
    counterBidAmount,
    fmsCounterBiddingEnabled,
  ])

  const bidStatus = useMemo(() => {
    if (['pending', 'countered'].includes(status)) {
      return 'pending'
    }
    // When status isn't returned we can infer the status from the presence of declinedAt.
    return status || declinedAt !== null ? 'declined' : 'pending'
  }, [status, declinedAt])

  return (
    <Card className='mt4 mb4' noPadding>
      {/* Display Bid Details in Top Half of Card */}
      <Box data-test='existing-bid-card--details-section' p={2}>
        <Grid container spacing={1}>
          <Grid item xs={10}>
            <Box>
              <Typography
                data-test='existing-bid-card--bid-amount'
                variant='subtitle2'
                className={bidStatus === 'declined' ? classes.bidDeclined : classes.bid}
                component='span'>
                {amount ? `You Bid ${formatCurrency(amount)}` : DISPLAY.empty}
                {bidStatus && (
                  <>
                    {' · '}
                    {capitalize(bidStatus)}
                  </>
                )}
              </Typography>
            </Box>
            <Box>
              {carrierDotNumber && (
                <Typography
                  data-test='existing-bid-card--carrier-name'
                  variant='subtitle2'
                  className={bidStatus === 'declined' ? classes.bidDeclined : ''}
                  noWrap>
                  Bid for {carrierLegalName}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={2} className={classes.editIcon}>
            {bidStatus !== 'declined' && (
              <IconButton
                data-test='existing-bid-card--edit-button'
                Icon={EditIcon}
                label='edit'
                color='tertiary'
                onClick={clickEditBidHandler}
                size='large'
              />
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Display Counterbid Details in Bottom Half of Card */}
      {offerCounterBid && (
        <Box
          data-test='existing-bid-card--counterbid-section'
          borderTop={`1px solid ${brandColors.gray1}`}
          bgcolor={brandColors.skyBlue0}
          p={2}>
          <CounterOfferDetails
            compact
            bid={bid}
            shipmentId={shipmentId}
            onClick={counterOfferDetailsOnClick}
            carrierIdForBid={carrierId}
          />
        </Box>
      )}
    </Card>
  )
}
